/** Session timestamp of the last automatic reload (loop guard). */
export const CHUNK_RELOAD_STORAGE_KEY = 'mp-app.stale-chunk-reload'

/**
 * At most one automatic reload per window. It used to be a flag cleared on
 * every boot, so a chunk that kept failing (phone holding an old index.html)
 * reloaded forever and the app looked frozen.
 */
const AUTO_RELOAD_WINDOW_MS = 60_000

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /loading chunk [\d]+ failed/i,
  /loading css chunk [\d]+ failed/i,
  /unable to preload css/i,
]

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false

  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : typeof (error as { message?: unknown }).message === 'string'
          ? String((error as { message: string }).message)
          : String(error)

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

/** True when a stale-chunk error may still trigger an automatic reload. */
export function canAutoReloadForChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0)
    return !last || Date.now() - last > AUTO_RELOAD_WINDOW_MS
  } catch {
    // No storage → no loop guard → never auto-reload; the user gets a Reload button.
    return false
  }
}

let pendingHref: (() => string | null) | null = null

/** Router hook-up: where an in-flight navigation is going (null when idle). */
export function setPendingHrefGetter(getter: () => string | null): void {
  pendingHref = getter
}

/** Cache-busted reload into the page being opened (a pending navigation), not the one still on screen. */
export function reloadLatestVersion(): void {
  const url = new URL(pendingHref?.() ?? window.location.href, window.location.origin)
  url.searchParams.set('_refresh', String(Date.now()))
  window.location.replace(url.toString())
}

/**
 * After a deploy, users may still have an old JS bundle that references deleted
 * chunk files. Reload once (cache-busted) to fetch the latest index + assets.
 *
 * Returns true when a reload was triggered.
 */
export function recoverFromStaleChunk(error: unknown): boolean {
  if (!isChunkLoadError(error) || !canAutoReloadForChunk()) return false

  try {
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()))
  } catch {
    return false
  }

  reloadLatestVersion()
  return true
}

export function registerChunkRecoveryHandlers(): void {
  window.addEventListener('unhandledrejection', (event) => {
    if (recoverFromStaleChunk(event.reason)) {
      event.preventDefault()
    }
  })

  window.addEventListener(
    'error',
    (event) => {
      if (recoverFromStaleChunk(event.error ?? event.message)) {
        event.preventDefault()
      }
    },
    true,
  )
}
